import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { ncbService } from '../services/ncbService';

const { FiStar, FiQuote, FiChevronLeft, FiChevronRight, FiInfo } = FiIcons;

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await ncbService.getTestimonials();
        // Filter out any entries that failed normalization
        setTestimonials(data.filter(t => t.reviewer_name || t.review_text));
        setError(null);
      } catch (err) {
        console.error('Testimonials load fail:', err);
        setError("Stories are temporarily unavailable.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const next = () => setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  useEffect(() => {
    if (testimonials.length <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-10 h-10 border-4 border-brand-olive/20 border-t-brand-olive rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="max-w-md mx-auto bg-white p-8 text-center border border-brand-olive/5 shadow-xl">
      <SafeIcon icon={FiInfo} className="text-brand-olive/20 text-3xl mx-auto mb-4" />
      <p className="text-brand-olive/60 text-[10px] font-black uppercase tracking-widest">{error}</p>
    </div>
  );

  if (testimonials.length === 0) return (
    <div className="text-center py-20 text-brand-olive/40 font-serif italic text-xl">
      Our guests' stories will appear here soon.
    </div>
  );

  const current = testimonials[currentIndex];

  return (
    <div className="relative max-w-4xl mx-auto px-4">
      <div className="overflow-hidden py-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="bg-white p-10 md:p-16 shadow-2xl relative border border-brand-olive/5 hover:border-brand-olive/30 hover:scale-[1.01] transition-all duration-500 group cursor-default"
          >
            <div className="absolute -top-4 left-10 w-10 h-10 bg-brand-olive flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-transform duration-500">
              <SafeIcon icon={FiQuote} className="text-white text-lg" />
            </div>
            
            <div className="flex gap-1 mb-8">
              {[...Array(Number(current.rating || 5))].map((_, i) => (
                <SafeIcon key={i} icon={FiStar} className="text-brand-olive text-sm fill-brand-olive" />
              ))}
            </div>

            <p className="text-brand-olive/80 text-xl md:text-2xl italic leading-relaxed mb-10 font-light">
              "{current.review_text}"
            </p>

            <div className="flex items-center gap-5">
              {current.profile_photo_url ? (
                <img 
                  src={current.profile_photo_url} 
                  alt={current.reviewer_name} 
                  className="w-16 h-16 rounded-full object-cover transition-all duration-700 shadow-md border-2 border-brand-cream" 
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-brand-cream flex items-center justify-center shadow-md border-2 border-brand-olive/5">
                  <SafeIcon icon={FiIcons.FiUser} className="text-brand-olive/20 text-2xl" />
                </div>
              )}
              <div>
                <h4 className="font-serif text-brand-olive font-bold uppercase tracking-widest text-sm">
                  {current.reviewer_name}
                </h4>
                <p className="text-brand-olive/50 text-[11px] uppercase tracking-widest mt-1">
                  {current.reviewer_title}
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {testimonials.length > 1 && (
        <div className="flex items-center justify-center gap-8 mt-4">
          <button onClick={prev} className="p-3 text-brand-olive/40 hover:text-brand-olive hover:scale-125 transition-all outline-none">
            <SafeIcon icon={FiChevronLeft} className="text-2xl" />
          </button>
          <div className="flex gap-3">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-all duration-500 ${currentIndex === i ? 'bg-brand-olive w-8' : 'bg-brand-olive/20 hover:bg-brand-olive/40'}`}
              />
            ))}
          </div>
          <button onClick={next} className="p-3 text-brand-olive/40 hover:text-brand-olive hover:scale-125 transition-all outline-none">
            <SafeIcon icon={FiChevronRight} className="text-2xl" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TestimonialCarousel;